import React, { useEffect, useRef, useState } from "react";

import TerrainViewer from "../terrain3d/TerrainViewer";
import {
  StepPanel, StepCollapsedBar, StepNavRow,
  authorDefaults, panelContentInsets, useBandHeight,
  REFERENCE_WIDTH, REFERENCE_HEIGHT,
} from "../territory_intro/StepPanel";

/**
 * CANEVAS D'ÉDITION — le cadre dans lequel l'auteur compose une étape.
 *
 * LE PROBLÈME QU'IL RÉSOUT. L'ancien éditeur affichait la carte dans une
 * moitié de modale, en portrait, pendant que l'écran d'atelier est un 16:9
 * dont un rail de 470 px masque la gauche. Deux cadres sans rapport : une
 * caméra capturée dans l'un ne pouvait pas cadrer l'autre. Agrandir la
 * fenêtre n'y changeait rien — c'était un problème de géométrie, pas de
 * taille.
 *
 * LA RÈGLE : le canevas est verrouillé au ratio de l'écran projeté, et tout ce
 * qui l'occupe est exprimé dans le repère de cet écran (1920x1080), jamais en
 * pixels de la fenêtre de l'auteur.
 *
 * COMMENT C'EST OBTENU. Le canevas monte un PLATEAU aux dimensions exactes de
 * l'écran projeté, réduit d'un seul `transform: scale()`, et y rend le VRAI
 * panneau d'étape (cf. territory_intro/StepPanel). Il dessinait auparavant une
 * imitation — un rectangle placé par des constantes JavaScript répétant ce que
 * la feuille de style dit déjà. Les deux descriptions divergeaient, et depuis
 * que le bandeau bas et le grand panneau ont une hauteur libre, aucune
 * constante ne pouvait plus dire la vérité : la hauteur d'un texte ne se
 * devine pas. L'auteur voit donc son écran, pas un croquis de son écran.
 *
 * LE PLATEAU NE REÇOIT AUCUN CLIC (`pointer-events: none`). Ce n'est pas de la
 * prudence : le canevas est AUSSI la surface sur laquelle on oriente la carte
 * à la souris. Un panneau cliquable y mangerait le geste de cadrage sur tout
 * son quart d'écran.
 */

const REFERENCE_RATIO = REFERENCE_WIDTH / REFERENCE_HEIGHT;

const StepCanvas = React.forwardRef(function StepCanvas(
  {
    territoryId, availableLayers, fogEnabled, step, previousStep, onCapturePose,
    placingPoint, onTerrainClick, onCatalogChange, onForgetPose, ghostMode = "full",
    // La séquence entière, et pas seulement un compte : le fantôme montre le
    // vrai bandeau d'avancement, qui est découpé en chapitres (cf.
    // territory_intro/chapters.js) — un total d'étapes ne suffit plus à le
    // dessiner.
    steps = [], stepIndex = 0, gameStatus = null,
  },
  viewerRef
) {
  const frameRef = useRef(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  // La largeur réelle du cadre pilote toute la mise à l'échelle : elle est
  // mesurée plutôt que devinée, parce qu'elle dépend de la fenêtre de
  // l'auteur et du repli de l'inspecteur.
  useEffect(() => {
    const element = frameRef.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setFrameWidth(element.clientWidth));
    observer.observe(element);
    setFrameWidth(element.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Un menu qui ne se ferme qu'en recliquant sur son propre bouton est un
  // piège classique : ici il se referme au premier clic ailleurs, ce qui est
  // le geste que fait spontanément quelqu'un qui a changé d'avis.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (event) => {
      if (!event.target.closest?.(".step-canvas-menu-wrap")) setMenuOpen(false);
    };
    const onKey = (event) => event.key === "Escape" && setMenuOpen(false);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Une étape sans cadrage capturé s'ouvrira, en séance, sur la vue par
  // défaut du territoire : le canevas doit donc l'y amener quand on arrive
  // dessus, sinon il montrerait le cadrage de l'étape précédente et mentirait
  // — exactement ce que cette refonte cherche à supprimer. Réglé sur
  // l'identité de l'étape, pour ne pas ramener la caméra pendant que l'auteur
  // cherche justement son cadrage.
  useEffect(() => {
    if (!step.camera) viewerRef.current?.fitTerritory({ animate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.step_id]);

  const scale = frameWidth ? frameWidth / REFERENCE_WIDTH : 0;

  // Même traduction mode d'auteur -> disposition que côté joueur, empruntée au
  // joueur : c'est la seule façon que les deux ne divergent pas.
  const { size, collapsed } = authorDefaults(step.panel_mode);
  const [bandRef, bandHeight] = useBandHeight(!collapsed && size === "bottom");

  // Les inserts sont exprimés dans le repère de l'écran projeté ; le viewer,
  // lui, travaille en pixels de son propre canvas — d'où la remise à l'échelle.
  const insets = panelContentInsets({ size, collapsed, bandHeight });
  const contentInsets = insets
    ? { left: (insets.left || 0) * scale, bottom: (insets.bottom || 0) * scale }
    : null;

  const applyPreviousPose = () => {
    if (previousStep?.camera) viewerRef.current?.applyPose(previousStep.camera, { animate: true });
  };

  return (
    <div className="step-canvas">
      <div className="step-canvas-frame" ref={frameRef} style={{ aspectRatio: REFERENCE_RATIO }}>
        {territoryId ? (
          <TerrainViewer
            ref={viewerRef}
            territoryId={territoryId}
            availableLayers={availableLayers}
            activeLayers={step.active_layers || []}
            layerSettings={step.layer_settings || {}}
            cameraPose={step.camera}
            // La caméra vole quand on change d'étape depuis le storyboard :
            // l'auteur voit l'enchaînement, pas une succession de cadrages
            // isolés. Elle ne vole pas pendant qu'il règle la vue courante,
            // ce que garantit la clé de pose (inchangée tant qu'on ne capture
            // pas) côté viewer.
            animateCamera
            fogEnabled={fogEnabled}
            points={step.kind === "points_of_interest" ? (step.points_of_interest?.points || []) : []}
            placementMode={placingPoint}
            onTerrainClick={onTerrainClick}
            onCatalogChange={onCatalogChange}
            contentInsets={contentInsets}
            // Le panneau du viewer est dimensionne en pixels : dans un cadre
            // plus petit que 1920, il occupait proportionnellement bien plus
            // de place qu'a la projection — donc le canevas montrait une carte
            // plus encombree que la realite. L'echelle le remet a sa taille
            // relative exacte, et les legendes demarrent repliees.
            uiScale={scale || 1}
            legendsOpen={false}
          />
        ) : (
          <div className="builder-empty">Choisissez d'abord un territoire pour cette page.</div>
        )}

        {/* LE PLATEAU. Aux dimensions de l'écran projeté, réduit d'un seul
            `scale` : le panneau s'y place tout seul, avec sa propre feuille de
            style, donc à sa place exacte. La classe `territory-intro-page` lui
            donne les jetons de couleur du module (déclarés dans
            TerrainViewer.css sur ce sélecteur) — sans elle, le panneau sortirait
            sans couleurs.

            Sur l'onglet Carte, il s'efface presque : en « grand panneau centré »
            il recouvre presque toute la carte, et l'auteur réglerait ses couches
            à l'aveugle. Assez visible pour dire où le contenu tombera, assez
            transparent pour laisser voir ce qu'on est venu régler. */}
        {ghostMode !== "hidden" && scale > 0 && (
          <div
            className={`step-canvas-stage territory-intro-page${ghostMode === "outline" ? " is-outline" : ""}`}
            style={{ transform: `scale(${scale})` }}
            aria-hidden="true"
          >
            {collapsed ? (
              <StepCollapsedBar
                steps={steps}
                stepIndex={stepIndex}
                nav={<StepNavRow isFirst={stepIndex === 0} isLast={stepIndex === steps.length - 1} />}
              />
            ) : (
              <StepPanel
                step={step}
                size={size}
                gameStatus={gameStatus}
                // Les graphiques d'étape lisent le datapack du territoire :
                // l'auteur voit donc ses vraies courbes pendant qu'il les
                // règle, pas un cadre gris. Aucune `selection` ici — le
                // plateau ne reçoit pas les clics, un graphique réglé sur
                // « suivre la sélection » montre son repli documenté.
                territoryId={territoryId}
                steps={steps}
                stepIndex={stepIndex}
                nav={<StepNavRow isFirst={stepIndex === 0} isLast={stepIndex === steps.length - 1} />}
                panelRef={bandRef}
              />
            )}
          </div>
        )}

        {/* CADRAGE. Une seule action reste visible — capturer — parce que
            c'est la seule qu'on fait à chaque étape. Recadrer sur le
            territoire ou reprendre la vue précédente sont des dépannages :
            trois boutons pleine largeur posés sur la carte la rendaient
            illisible pour un service rendu deux fois par module. */}
        {territoryId && (
          <div className="step-canvas-controls">
            {!step.camera && <span className="step-canvas-chip">Cadrage non capturé</span>}
            <button
              type="button"
              className="step-canvas-capture"
              onClick={onCapturePose}
              title="Enregistrer la vue actuelle comme cadrage de cette étape"
            >
              📷 {step.camera ? "Recapturer" : "Capturer"}
            </button>
            <div className="step-canvas-menu-wrap">
              <button
                type="button"
                className={`step-canvas-menubtn${menuOpen ? " is-open" : ""}`}
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-label="Autres options de cadrage"
                title="Autres options de cadrage"
              >
                ⋯
              </button>
              {menuOpen && (
                <div className="step-canvas-menu">
                  <button
                    type="button"
                    onClick={() => { viewerRef.current?.fitTerritory(); setMenuOpen(false); }}
                  >
                    Recadrer sur le territoire entier
                  </button>
                  <button
                    type="button"
                    disabled={!previousStep?.camera}
                    onClick={() => { applyPreviousPose(); setMenuOpen(false); }}
                  >
                    Reprendre le cadrage de l'étape précédente
                  </button>
                  <button
                    type="button"
                    disabled={!step.camera}
                    onClick={() => { onForgetPose?.(); setMenuOpen(false); }}
                  >
                    Oublier le cadrage de cette étape
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <p className="step-canvas-caption">
        Cadre 16:9 à l'échelle de l'écran projeté.
      </p>
    </div>
  );
});

export default StepCanvas;
